import { Checkbox, Dropdown, DropdownMenuItemType, IDropdownOption, MessageBar, MessageBarType } from '@fluentui/react';
import * as React from 'react';
import { AutomationIds } from '../../../models/constants/automationIds';
import { SourceApps } from '../../../models/constants/constants';
import { RequestSeverity, FreelanceRequest } from '../models/requestModels';
import { getFieldClassNames, getFieldStyles } from '../styles/requestDetailsStyles';
import { IThemedProp } from '../styles/themesUI';
import { getString } from '../../../../web/utils/stringLoader';

interface ISeverityProps {
  sourceApp: SourceApps;
  isEmailFieldDisabled: boolean;
  isSeverityEditingMode: boolean;
  dispatchUpdateRequest: (properties: Partial<FreelanceRequest>) => void;
  dispatchUpdateBusinessImpactConfirmed: (checked: boolean) => void;
  dispatchCustomerAvailabilityConfirmed: (checked: boolean) => void;
}

interface ISeverityStateProps {
  isUnifiedPremier: boolean;
  isUnifiedEnterprisePremier: boolean;
  isNonUnifiedPremier: boolean;
  businessImpactConfirmed: boolean
  customerAvailabilityConfirmed: boolean;
  severity: RequestSeverity;
}

type ISeverity = ISeverityProps & ISeverityStateProps & IThemedProp;

export class Severity extends React.Component<ISeverity> {
  public render(): React.ReactNode {
    const fieldStyles = getFieldClassNames(getFieldStyles, {
      theme: this.props.theme,
      sourceApp: this.props.sourceApp,
    });

    return this.props.isSeverityEditingMode && (
      <>
        <div className={fieldStyles.severityContainer}>
          {this.props.isUnifiedPremier && this.renderCriticalSubSection()}
          {this.props.isUnifiedEnterprisePremier && this.renderSeverity1ABCSubSection()}
          {(this.props.isNonUnifiedPremier && this.renderSeverityABCSubSection())}
        </div>
      </>
    );
  }
  private getCriticalCheckboxValue = (): boolean => {
    if (this.props.severity === null || this.props.severity === undefined) {
      this.props.dispatchUpdateRequest({ Severity: RequestSeverity.None });
    } else if (this.props.severity === RequestSeverity.SevA) {
      // if the tenant subscription changed from Premier to Unified
      // and there is existing SevA ticket, we dispatch severity as critical.
      // Cancel editing won't do any change.
      this.props.dispatchUpdateRequest({ Severity: RequestSeverity.Critical });
    }
    return this.props.severity === RequestSeverity.Critical;
   };

  private onCriticalCheckboxChange = (_event: React.FormEvent<HTMLElement>, checked: boolean): void => {
    this.props.dispatchUpdateRequest(checked
                                ? { Severity: RequestSeverity.Critical }
                                : { Severity: RequestSeverity.None }
                            );
  };

  private getSeverityABCToDropdownKey = (): RequestSeverity | undefined => {
    if (!this.props.severity) {
      this.props.dispatchUpdateRequest({ Severity: RequestSeverity.SevB });
    } else if (this.props.severity === RequestSeverity.Critical) {
    // if the tenant subscription changed from Unified to Premier
    // and there is existing Critical ticket, we dispatch severity as SevA.
    // Cancel editing won't do any change.
    this.props.dispatchUpdateRequest({ Severity: RequestSeverity.SevA });
    }
    return this.props.severity ? this.props.severity : RequestSeverity.SevB;
  };

  private getSeverity1ABCToDropdownKey = (): RequestSeverity | undefined => {
    if (!this.props.severity) {
      this.props.dispatchUpdateRequest({ Severity: RequestSeverity.SevB });
    } else if (this.props.severity === RequestSeverity.Critical) {
      // if the tenant subscription changed from Unified to Premier
      // and there is existing Critical ticket, we dispatch severity as SevA.
      // Cancel editing won't do any change.
      this.props.dispatchUpdateRequest({ Severity: RequestSeverity.SevA });
    }
    return this.props.severity ? this.props.severity : RequestSeverity.SevB;
  };

  private onSeverityDropdownChange = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    const severity = item.key as RequestSeverity;
    this.props.dispatchUpdateRequest({ Severity: severity });
  };

  private getPlaceHolder = (): string => {
    return !!this.props.severity ? getString(`requestSeverity_${RequestSeverity[this.props.severity]}`) : getString('severity_SevABCDropdownPlaceholderText');
  };

  private onRenderSeverityOption = (option: IDropdownOption): JSX.Element => {
    return (
      <div key={option.key.toLocaleString()}>
        <div>
          {option.text}
        </div>
        {option.data && <div>{option.data}</div>}
      </div>
    );
  };

  private onBusinessImpactConfirmationCheckBoxChange = (
    _event: React.FormEvent<HTMLElement>,
    checked: boolean
  ): void => {
    this.props.dispatchUpdateBusinessImpactConfirmed(checked);
  };

  private onCustomerAvailabilityConfirmationChange = (_event: React.FormEvent<HTMLElement>, checked: boolean): void => {
    this.props.dispatchCustomerAvailabilityConfirmed(checked);
  };

  private renderSeverityConfirmationSubSection = (): React.ReactNode => {
    const fieldStyles = getFieldClassNames(getFieldStyles, {
      theme: this.props.theme,
      sourceApp: this.props.sourceApp,
    });

    return (
      <div className={fieldStyles.serveritySubContainer}>
        <div className={fieldStyles.severityLabel}>
          <span>{getString('severity_ConfirmationHeadingText')}</span>
          <span className={fieldStyles.severityRequiredLabel}>{getString('severity_RequiredAsterisk')}</span>
        </div>
        <div>
          <Checkbox
            data-automation-id={AutomationIds.BusinessImpactConfirmed}
            key="BusinessImpactConfirmed"
            label={getString('severity_ConfirmationBusinessImpactConfirmedText')}
            onChange={this.onBusinessImpactConfirmationCheckBoxChange}
            checked={this.props.businessImpactConfirmed}
            className={fieldStyles.severityCheckBox}
          />
          <Checkbox
            data-automation-id={AutomationIds.CustomerAvailabilityConfirmed}
            key="CustomerAvailabilityConfirmed"
            label={getString('severity_ConfirmationCustomerAvailabilityConfirmedText')}
            onChange={this.onCustomerAvailabilityConfirmationChange}
            checked={this.props.customerAvailabilityConfirmed}
            className={fieldStyles.severityCheckBox}
          />
        </div>
      </div>
    );
  };

  private renderCriticalSubSection = (): React.ReactNode => {
    const fieldStyles = getFieldClassNames(getFieldStyles, {
      theme: this.props.theme,
      sourceApp: this.props.sourceApp,
    });

    return (
      <div>
        <div className={fieldStyles.severityLabel}>
          {getString('severity_CriticalHeaderText')}
        </div>
        <div className={fieldStyles.fieldInputGroup}>
          <Checkbox
            label={getString('severity_CriticalText')}
            checked={this.getCriticalCheckboxValue()}
            onChange={this.onCriticalCheckboxChange}
            disabled={this.props.isEmailFieldDisabled}
            className={fieldStyles.severityCheckBox}
          />
        </div>
        {this.props.severity === RequestSeverity.Critical && (
          <>
            <MessageBar
              messageBarType={MessageBarType.info}
              className={fieldStyles.serverityMessageBar}
            >
              <strong>{getString('severity_CriticalMessageTextCriticalIssue')}</strong>
              {getString('severity_CriticalMessageTextDescription')}
            </MessageBar>
            {this.renderSeverityConfirmationSubSection()}
          </>
        )}
      </div>
    );
  };

  private renderSeverityABCSubSection = (): React.ReactNode => {
    const generateSeverityABCOptions = (): IDropdownOption[] => {
      const options: IDropdownOption[] = [];
      options.push(
        {
          key: RequestSeverity.SevA,
          text: getString('severity_SevAOptionText'),
          title: getString('severity_SevALabelText'),
          disabled: this.props.isEmailFieldDisabled,
        },
        {
          key: RequestSeverity.SevB,
          text: getString('severity_SevBOptionText'),
          title: getString('severity_SevBLabelText'),
        },
        {
          key: RequestSeverity.SevC,
          text: getString('severity_SevCOptionText'),
          title: getString('severity_SevCLabelText'),
        }
      );
      return options;
    };

    const fieldStyles = getFieldClassNames(getFieldStyles, {
      theme: this.props.theme,
      sourceApp: this.props.sourceApp,
    });

    return (
      <div>
        <div className={fieldStyles.severityLabel}>
          {getString('severity_SevABCDropdownHeaderText')}
        </div>
        <table className={fieldStyles.severityABCDropdown}>
          <tbody>
            <tr>
              <td>
                <Dropdown
                  placeholder={this.getPlaceHolder()}
                  selectedKey={this.getSeverityABCToDropdownKey()}
                  onChange={this.onSeverityDropdownChange}
                  options={generateSeverityABCOptions()}
                />
              </td>
            </tr>
          </tbody>
        </table>
        {this.props.severity === RequestSeverity.SevA && (
          <>
            <MessageBar
              messageBarType={MessageBarType.info}
              className={fieldStyles.serverityMessageBar}
            >
              <strong>{getString('severity_CriticalMessageTextCriticalIssue')}</strong>
              {getString('severity_CriticalMessageTextDescription')}
            </MessageBar>
            {this.renderSeverityConfirmationSubSection()}
          </>
        )}
      </div>
    );
  };

  private renderSeverity1ABCSubSection = (): React.ReactNode => {
    const fieldStyles = getFieldClassNames(getFieldStyles, {
      theme: this.props.theme,
      sourceApp: this.props.sourceApp,
    });

    const generateSeverity1ABCOptions = (): IDropdownOption[] => {
      const options: IDropdownOption[] = [];
      options.push(
        {
          key: 'Header',
          text: getString('severity_SevABCHeaderText'),
          itemType: DropdownMenuItemType.Header,
        },
        {
          key: RequestSeverity.SevA,
          text: getString('severity_SevAOptionTextUE'),
          title: getString('severity_SevALabelText'),
          data: getString('severity_SevAOptionData'),
          disabled: this.props.isEmailFieldDisabled,
        },
        {
          key: RequestSeverity.SevB,
          text: getString('severity_SevBOptionTextUE'),
          title: getString('severity_SevBLabelText'),
          data: getString('severity_SevBOptionData'),
        },
        {
          key: RequestSeverity.SevC,
          text: getString('severity_SevCOptionTextUE'),
          title: getString('severity_SevCLabelText'),
          data: getString('severity_SevCOptionData'),
        },
        {
          key: 'Header2',
          text: getString('severity_Sev1HeaderText'),
          itemType: DropdownMenuItemType.Header,
        },
        {
          key: RequestSeverity.Sev1,
          text: getString('severity_Sev1OptionTextUE'),
          title: getString('severity_Sev1LabelText'),
          data: getString('severity_Sev1OptionData'),
          disabled: this.props.isEmailFieldDisabled,
        }
      );
      return options;
    };

    return (
      <div>
        <div className={fieldStyles.severityLabel}>
          {getString('severity_SevABCDropdownHeaderText')}
        </div>
        <table>
          <tbody>
            <tr>
              <td>
                <Dropdown
                  placeholder={this.getPlaceHolder()}
                  selectedKey={this.getSeverity1ABCToDropdownKey()}
                  onRenderOption={this.onRenderSeverityOption}
                  onChange={this.onSeverityDropdownChange}
                  options={generateSeverity1ABCOptions()}
                  calloutProps={{ calloutWidth: 405 }}
                  className={fieldStyles.severityDropdown}
                />
              </td>
            </tr>
          </tbody>
        </table>
        {(this.props.severity === RequestSeverity.SevA || this.props.severity === RequestSeverity.Sev1) && (
          <>
            <MessageBar
              messageBarType={MessageBarType.info}
              className={fieldStyles.serverityMessageBar}
            >
              <div className={fieldStyles.severityText}>
                <strong>{getString('severity_CriticalMessageTextCriticalIssue')}</strong>
                {getString('severity_CriticalMessageTextDescription')}
              </div>
            </MessageBar>
            {this.renderSeverityConfirmationSubSection()}
          </>
        )}
      </div>
    );
  };
}
